import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { getDatabaseStats, DatabaseStats } from '../services/api';

export default function DatabaseStatsComponent() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const statsData = await getDatabaseStats();
      setStats(statsData);
    } catch (err: any) {
      setError(t('dbStats.failedToFetch'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {t('dbStats.noStatsAvailable')}
      </Alert>
    );
  }

  const totalInserts = stats.table_stats.reduce((sum, table) => sum + table.inserts, 0);
  const totalUpdates = stats.table_stats.reduce((sum, table) => sum + table.updates, 0);
  const totalDeletes = stats.table_stats.reduce((sum, table) => sum + table.deletes, 0);
  const totalLiveTuples = stats.table_stats.reduce((sum, table) => sum + table.live_tuples, 0);
  const totalDeadTuples = stats.table_stats.reduce((sum, table) => sum + table.dead_tuples, 0);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('dbStats.title')}
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom>
        {t('dbStats.description')}
        {' '}{t('dbStats.lastUpdated')} {new Date(stats.timestamp).toLocaleString()}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2, mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('dbStats.totalInserts')}
              </Typography>
              <Typography variant="h4">
                {totalInserts.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('dbStats.totalUpdates')}
              </Typography>
              <Typography variant="h4">
                {totalUpdates.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('dbStats.totalDeletes')}
              </Typography>
              <Typography variant="h4">
                {totalDeletes.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('dbStats.liveTuples')}
              </Typography>
              <Typography variant="h4">
                {totalLiveTuples.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('dbStats.deadTuples')}
              </Typography>
              <Typography variant="h4">
                {totalDeadTuples.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('dbStats.tableStatistics')}
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('dbStats.schema')}</TableCell>
                <TableCell>{t('dbStats.table')}</TableCell>
                <TableCell align="right">{t('dbStats.inserts')}</TableCell>
                <TableCell align="right">{t('dbStats.updates')}</TableCell>
                <TableCell align="right">{t('dbStats.deletes')}</TableCell>
                <TableCell align="right">{t('dbStats.liveTuples')}</TableCell>
                <TableCell align="right">{t('dbStats.deadTuples')}</TableCell>
                <TableCell align="right">{t('dbStats.health')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.table_stats.map((table, index) => {
                const deadRatio = table.live_tuples > 0 
                  ? table.dead_tuples / table.live_tuples 
                  : 0;
                
                let healthColor: "success" | "warning" | "error" = "success";
                let healthText = t('dbStats.healthGood');

                if (deadRatio > 0.1) {
                  healthColor = "warning";
                  healthText = t('dbStats.healthNeedsVacuum');
                }
                if (deadRatio > 0.2) {
                  healthColor = "error";
                  healthText = t('dbStats.healthCritical');
                }

                return (
                  <TableRow key={index} hover>
                    <TableCell>{table.schemaname}</TableCell>
                    <TableCell>
                      <strong>{table.tablename}</strong>
                    </TableCell>
                    <TableCell align="right">{table.inserts.toLocaleString()}</TableCell>
                    <TableCell align="right">{table.updates.toLocaleString()}</TableCell>
                    <TableCell align="right">{table.deletes.toLocaleString()}</TableCell>
                    <TableCell align="right">{table.live_tuples.toLocaleString()}</TableCell>
                    <TableCell align="right">{table.dead_tuples.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={healthText}
                        color={healthColor}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            {t('common.refreshing')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}